import { BodyLarge, FlexCol, FlexLg } from 'components/Elements';
import React from 'react';
import styled from 'styled-components';
import { sizes, theme } from 'theme';
import AccordionPanelItem, { AccordionPanelItemProps } from './AccordionPanelItem';
import { AccordionPanelHeaderTitle } from './AccordionPanelTitle';
import ArrowUpIcon from 'assets/icons/controls/ArrowUpIcon';
import ArrowDownIcon from 'assets/icons/controls/ArrowDownIcon';

export type AccordionPanelProps = {
    headerTitle: string;
    headerDescription?: string;
    openPanel?: boolean;
    panelCompleted: boolean;
    completedItemsText: string;
    panelItems: AccordionPanelItemProps[];
};
export default function AccordionPanel({
    headerTitle,
    headerDescription,
    openPanel,
    panelCompleted,
    completedItemsText,
    panelItems,
}: AccordionPanelProps): JSX.Element {
    const [panelIsOpen, setPanelIsOpen] = React.useState<boolean>(false);
    React.useEffect(() => setPanelIsOpen(openPanel ?? false), [openPanel]);

    const reactId = React.useId();
    const buttonId = `${reactId}-button`;
    const regionId = `${reactId}-region`;

    const regionRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const el = regionRef.current;
        if (!el) return;

        if (panelIsOpen) {
            el.removeAttribute('inert');
            el.removeAttribute('aria-hidden');
        } else {
            el.setAttribute('inert', ''); // blocks tab/focus
            el.setAttribute('aria-hidden', 'true'); // hide from SRs
            if (el.contains(document.activeElement)) {
                document.getElementById(buttonId)?.focus();
            }
        }
    }, [panelIsOpen, buttonId]);

    return (
        <AccordionPanelContainer complete={panelCompleted}>
            <h3>
                <AccordionHeaderButton
                    id={buttonId}
                    aria-expanded={panelIsOpen}
                    aria-controls={regionId}
                    aria-label={panelIsOpen ? `${headerTitle}` : `${headerTitle}`}
                    onClick={() => setPanelIsOpen((v) => !v)}
                >
                    <PanelHeader>
                        <AccordionPanelHeaderTitle title={headerTitle} description={headerDescription} />
                        <Container>
                            <CompletedItems $bold>{completedItemsText}</CompletedItems>
                            {panelIsOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
                        </Container>
                    </PanelHeader>
                </AccordionHeaderButton>
            </h3>

            <Collapse id={regionId} role="region" aria-labelledby={buttonId} $open={panelIsOpen} ref={regionRef}>
                <div className="inner">
                    <FlexCol>
                        {panelItems.map((item, index) => (
                            <AccordionPanelItem
                                key={index}
                                action={item.action}
                                aria={item.aria}
                                content={item.content}
                            />
                        ))}
                    </FlexCol>
                </div>
            </Collapse>
        </AccordionPanelContainer>
    );
}

const CompletedItems = styled(BodyLarge)`
    width: max-content;
`;

const Container = styled(FlexLg)`
    align-items: center;
`;

const PanelHeader = styled(FlexLg)`
    justify-content: space-between;
    align-items: center;
`;

const Collapse = styled.div<{ $open: boolean }>`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    transition: grid-template-rows 400ms ease;

    .inner {
        overflow: hidden;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const AccordionPanelContainer = styled.div<{ complete?: boolean }>`
    cursor: pointer;
    padding: ${sizes.spacingMd};
    box-shadow: 2px 4px 6px rgba(0, 0, 0, 0.1);
    background-color: ${({ complete }) =>
        complete ? theme.learnerActivityCard.completedBackground : theme.cardBackground};
    border-radius: ${sizes.borderRadiusLg};
    border: 1px solid ${({ complete }) => (complete ? theme.notificationSuccessBorder : theme.cardBorder)};
`;

const AccordionHeaderButton = styled.button`
    all: unset;
    display: block;
    width: 100%;
    cursor: pointer;
    padding: ${sizes.spacingSm};
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;

    border: 1px solid transparent;
    border-bottom-color: ${theme.cardBorder};
    border-radius: ${sizes.borderRadiusLg};
    background-color: inherit;

    transition:
        background-color 150ms ease,
        border-color 150ms ease,
        box-shadow 150ms ease;

    &:hover {
        background-color: ${theme.secondaryButtonHoverBackgroundColour};
        border-color: ${theme.secondaryButtonBorderColour};
    }

    &:focus-visible {
        background-color: ${theme.cardNavigation.backgroundFocused};
        border-color: ${theme.cardNavigation.borderFocused};
        outline: none;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;
